import { useEffect, useRef, useState } from 'react';
import DevicePerformance from '../utils/devicePerformance';

const SpaceCanvas = () => {
    const canvasRef = useRef(null);
    const animationRef = useRef(null);
    const starsRef = useRef([]);
    const shootingStarsRef = useRef([]);
    const nebulaeRef = useRef([]);
    const scrollSpeedRef = useRef(0);
    const lastScrollYRef = useRef(0);
    const mouseXRef = useRef(0);
    const mouseYRef = useRef(0);
    
    // Performance detection
    const [shouldRender, setShouldRender] = useState(true);
    const [performanceSettings, setPerformanceSettings] = useState(null);

    const getStarColor = () => {
        const colors = [
            '#b5fed9',
            '#98cbb4',
            '#7ba098',
            '#f0f7f4',
            '#e6f4ea'
        ];
        return colors[Math.floor(Math.random() * colors.length)];
    };

    const getNebulaColor = () => {
        const colors = [
            '45, 212, 191',   // bright teal
            '20, 184, 166',   // medium teal
            '7, 21, 32',      // deep navy for contrast
            '240, 247, 244',  // pale highlight
            '14, 20, 30',     // darker charcoal
            '147, 51, 234',   // velvet purple - bright
            '126, 34, 206',   // velvet purple - medium
            '88, 28, 135'     // velvet purple - deep
        ];
        return colors[Math.floor(Math.random() * colors.length)];
    };

    const initStars = (canvas) => {
        // Use performance-based particle count
        const maxStars = performanceSettings ? performanceSettings.particleCount : 600;
        const starCount = Math.min(maxStars, Math.floor((canvas.width * canvas.height) / 1200));
        starsRef.current = [];
        
        for (let i = 0; i < starCount; i++) {
            starsRef.current.push({
                x: Math.random() * canvas.width,
                y: Math.random() * canvas.height,
                z: Math.random() * 1500 + 500,
                speed: 0.02 + Math.random() * 0.08,
                size: Math.random() * 1.2 + 0.3,
                brightness: Math.random() * 0.2 + 0.1,
                color: getStarColor(),
                twinkleSpeed: Math.random() * 0.004 + 0.001,
                twinkleOffset: Math.random() * Math.PI * 2,
                hasGlow: Math.random() > 0.8
            });
        }
    };

    const initShootingStars = (canvas) => {
        shootingStarsRef.current = [];
        // Reduce shooting stars on lower-end devices
        const count = performanceSettings && performanceSettings.quality === 'low' ? 1 : 2;
        for (let i = 0; i < count; i++) {
            createShootingStar(canvas);
        }
    };

    const createShootingStar = (canvas) => {
        const side = Math.floor(Math.random() * 4);
        let x, y, speedX, speedY;
        
        switch(side) {
            case 0:
                x = Math.random() * canvas.width;
                y = -20;
                speedX = (Math.random() - 0.5) * 6;
                speedY = Math.random() * 6 + 3;
                break;
            case 1:
                x = canvas.width + 20;
                y = Math.random() * canvas.height * 0.7;
                speedX = -Math.random() * 6 - 3;
                speedY = (Math.random() - 0.5) * 3;
                break;
            case 2:
                x = Math.random() * canvas.width;
                y = canvas.height + 20;
                speedX = (Math.random() - 0.5) * 6;
                speedY = -Math.random() * 6 - 3;
                break;
            case 3:
                x = -20;
                y = Math.random() * canvas.height * 0.7;
                speedX = Math.random() * 6 + 3;
                speedY = (Math.random() - 0.5) * 3;
                break;
        }
        
        shootingStarsRef.current.push({
            x, y,
            speedX, speedY,
            length: Math.random() * 40 + 30,
            size: Math.random() * 1.5 + 0.5,
            life: 1,
            decay: Math.random() * 0.015 + 0.005,
            color: `hsla(160, 100%, 85%, 0.8)`,
            trail: []
        });
    };

    const initNebulae = (canvas) => {
        nebulaeRef.current = [];
        
        // Detect mobile device
        const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || window.innerWidth <= 768;
        
        // Disable nebulae (colored particles) on mobile devices
        if (isMobile) {
            return;
        }
        
        // Adjust nebula count based on performance (desktop only)
        let nebulaCount = 3;
        if (performanceSettings) {
            if (performanceSettings.quality === 'low') nebulaCount = 1;
            else if (performanceSettings.quality === 'medium') nebulaCount = 2;
        }
        
        for (let i = 0; i < nebulaCount; i++) {
            nebulaeRef.current.push({
                x: Math.random() * canvas.width,
                y: Math.random() * canvas.height,
                size: Math.random() * 300 + 100,
                speed: Math.random() * 0.06 + 0.02,
                color: getNebulaColor(),
                // increase density range for stronger, higher-contrast haze
                density: Math.random() * 0.35 + 0.06,
                rotation: Math.random() * Math.PI * 2,
                rotationSpeed: (Math.random() - 0.5) * 0.0008
            });
        }
    };

    const updateStars = (ctx, canvas, time) => {
        starsRef.current.forEach(star => {
            star.z -= star.speed + scrollSpeedRef.current * 2.5;
            
            const parallaxX = (mouseXRef.current - canvas.width / 2) * 0.0003 * (star.z / 1000);
            const parallaxY = (mouseYRef.current - canvas.height / 2) * 0.0003 * (star.z / 1000);
            
            const perspective = 800 / star.z;
            star.screenX = (star.x + parallaxX - canvas.width / 2) * perspective + canvas.width / 2;
            star.screenY = (star.y + parallaxY - canvas.height / 2) * perspective + canvas.height / 2;
            star.screenSize = star.size * perspective;
            
            star.currentBrightness = star.brightness * 
                (0.8 + 0.2 * Math.sin(time * star.twinkleSpeed + star.twinkleOffset));
            
            if (star.z <= 0.1 || 
                star.screenX < -100 || star.screenX > canvas.width + 100 ||
                star.screenY < -100 || star.screenY > canvas.height + 100) {
                star.x = Math.random() * canvas.width;
                star.y = Math.random() * canvas.height;
                star.z = 2000;
                star.screenX = null;
                star.screenY = null;
            }
        });
    };

    const updateShootingStars = (canvas) => {
        shootingStarsRef.current.forEach((star, index) => {
            star.trail.unshift({ x: star.x, y: star.y });
            if (star.trail.length > 8) {
                star.trail.pop();
            }
            
            star.x += star.speedX;
            star.y += star.speedY;
            star.life -= star.decay;
            
            if (star.life <= 0 || 
                star.x < -200 || star.x > canvas.width + 200 ||
                star.y < -200 || star.y > canvas.height + 200) {
                shootingStarsRef.current.splice(index, 1);
                if (Math.random() < 0.3) {
                    setTimeout(() => createShootingStar(canvas), Math.random() * 2500);
                }
            }
        });
        
        if (shootingStarsRef.current.length < 2 && Math.random() < 0.002) {
            createShootingStar(canvas);
        }
    };

    const updateNebulae = (canvas) => {
        nebulaeRef.current.forEach(nebula => {
            nebula.y += nebula.speed;
            nebula.rotation += nebula.rotationSpeed;
            
            if (nebula.y > canvas.height + nebula.size) {
                nebula.y = -nebula.size;
                nebula.x = Math.random() * canvas.width;
            }
        });
    };

    const drawStars = (ctx) => {
        starsRef.current.forEach(star => {
            if (!star.screenX || !star.screenY) return;
            
            const alpha = star.currentBrightness * (600 / star.z);
            const starAlpha = Math.min(alpha, 0.7);
            
            if (star.hasGlow && star.screenSize > 0.4) {
                const gradient = ctx.createRadialGradient(
                    star.screenX, star.screenY, 0,
                    star.screenX, star.screenY, star.screenSize * 1.8
                );
                
                gradient.addColorStop(0, `${star.color.replace(')', `, ${starAlpha * 0.4})`).replace('rgb', 'rgba')}`);
                gradient.addColorStop(0.5, `${star.color.replace(')', `, ${starAlpha * 0.1})`).replace('rgb', 'rgba')}`);
                gradient.addColorStop(1, `${star.color.replace(')', `, 0)`).replace('rgb', 'rgba')}`);
                
                ctx.fillStyle = gradient;
                ctx.beginPath();
                ctx.arc(star.screenX, star.screenY, star.screenSize * 1.8, 0, Math.PI * 2);
                ctx.fill();
            }
            
            ctx.beginPath();
            ctx.fillStyle = `${star.color.replace(')', `, ${starAlpha * 0.8})`).replace('rgb', 'rgba')}`;
            ctx.arc(star.screenX, star.screenY, star.screenSize * 0.6, 0, Math.PI * 2);
            ctx.fill();
        });
    };

    const drawShootingStars = (ctx) => {
        shootingStarsRef.current.forEach(star => {
            ctx.save();
            ctx.globalAlpha = star.life;
            
            if (star.trail.length > 1) {
                ctx.strokeStyle = star.color;
                ctx.lineCap = 'round';
                ctx.lineJoin = 'round';
                
                for (let i = 0; i < star.trail.length - 1; i++) {
                    const point = star.trail[i];
                    const nextPoint = star.trail[i + 1];
                    const progress = i / star.trail.length;
                    const width = star.size * (1 - progress);
                    
                    ctx.lineWidth = width;
                    ctx.beginPath();
                    ctx.moveTo(point.x, point.y);
                    ctx.lineTo(nextPoint.x, nextPoint.y);
                    ctx.stroke();
                }
            }
            
            ctx.beginPath();
            ctx.fillStyle = star.color;
            ctx.arc(star.x, star.y, star.size / 2, 0, Math.PI * 2);
            ctx.fill();
            
            ctx.restore();
        });
    };

    const drawNebulae = (ctx) => {
        nebulaeRef.current.forEach(nebula => {
            ctx.save();
            ctx.translate(nebula.x, nebula.y);
            ctx.rotate(nebula.rotation);
            
            const gradient = ctx.createRadialGradient(
                0, 0, 0,
                0, 0, nebula.size
            );
            // stronger inner opacity and smoother falloff for more contrast
            gradient.addColorStop(0, `rgba(${nebula.color}, ${Math.min(nebula.density * 1.2, 1)})`);
            gradient.addColorStop(0.5, `rgba(${nebula.color}, ${Math.max(nebula.density * 0.5, 0.02)})`);
            gradient.addColorStop(1, `rgba(${nebula.color}, 0)`);

            // draw with additive blend for brighter nebula highlights
            ctx.globalCompositeOperation = 'lighter';
            // apply slight blur for smooth haze (browser support permitting)
            try { ctx.filter = 'blur(8px)'; } catch (e) {}

            ctx.fillStyle = gradient;
            ctx.beginPath();
            ctx.arc(0, 0, nebula.size, 0, Math.PI * 2);
            ctx.fill();

            // restore filter and composition
            try { ctx.filter = 'none'; } catch (e) {}
            ctx.globalCompositeOperation = 'source-over';
            
            ctx.restore();
        });
    };

    const animate = (time) => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        
        const ctx = canvas.getContext('2d');
        
        if (!ctx) return;
        
        // Detect mobile device
        const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || window.innerWidth <= 768;
        
        // Darker background on mobile, normal on desktop
        const gradient = ctx.createLinearGradient(0, 0, 0, canvas.height);
        if (isMobile) {
            gradient.addColorStop(0, '#040609');
            gradient.addColorStop(1, '#060606');
        } else {
            gradient.addColorStop(0, '#0F172A');
            gradient.addColorStop(1, '#0b0b0b');
        }
        ctx.fillStyle = gradient;
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        
        updateNebulae(canvas);
        updateStars(ctx, canvas, time);
        updateShootingStars(canvas);
        
        drawNebulae(ctx);
        drawStars(ctx);
        drawShootingStars(ctx);
        
        scrollSpeedRef.current *= 0.94;
        if (scrollSpeedRef.current < 0.01) scrollSpeedRef.current = 0;
        
        animationRef.current = requestAnimationFrame(animate);
    };

    const resizeCanvas = () => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        
        canvas.width = window.innerWidth;
        canvas.height = window.innerHeight;
        initStars(canvas);
        initNebulae(canvas);
    };

    const trackScroll = () => {
        const currentScrollY = window.scrollY;
        const delta = currentScrollY - lastScrollYRef.current;
        scrollSpeedRef.current = Math.min(Math.abs(delta) * 0.12, 2.5);
        lastScrollYRef.current = currentScrollY;
    };

    useEffect(() => {
        // Check device performance on mount
        const settings = DevicePerformance.getRecommendedSettings();
        setPerformanceSettings(settings);
        setShouldRender(settings.enabled);
        
        if (!settings.enabled) {
            console.log('🚫 Animation disabled: Device performance too low');
        } else {
            console.log('✅ Animation enabled with settings:', settings);
        }
    }, []);

    useEffect(() => {
        // Don't render animation if device can't handle it
        if (!shouldRender || !performanceSettings) return;
        
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        resizeCanvas();
        initShootingStars(canvas);

        const handleResize = () => resizeCanvas();
        const handleScroll = () => trackScroll();
        const handleMouseMove = (e) => {
            mouseXRef.current = e.clientX;
            mouseYRef.current = e.clientY;
        };
        const handleTouchMove = (e) => {
            if (e.touches.length > 0) {
                mouseXRef.current = e.touches[0].clientX;
                mouseYRef.current = e.touches[0].clientY;
            }
        };

        window.addEventListener('resize', handleResize);
        window.addEventListener('scroll', handleScroll);
        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('touchmove', handleTouchMove);

        animationRef.current = requestAnimationFrame(animate);

        return () => {
            window.removeEventListener('resize', handleResize);
            window.removeEventListener('scroll', handleScroll);
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('touchmove', handleTouchMove);
            
            if (animationRef.current) {
                cancelAnimationFrame(animationRef.current);
            }
        };
    }, [shouldRender, performanceSettings]);

    // Don't render canvas if device can't handle animation
    if (!shouldRender) {
        return (
            <div
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    zIndex: 1,
                    pointerEvents: 'none',
                    background: 'linear-gradient(135deg, #0F172A 0%, #1a1a1a 100%)'
                }}
            />
        );
    }

    return (
        <canvas
            ref={canvasRef}
            id="spaceCanvas"
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                zIndex: 1,
                pointerEvents: 'none',
                background: 'linear-gradient(135deg, #0F172A 0%, #1a1a1a 100%)'
            }}
        />
    );
};

export default SpaceCanvas;